/**
 * ALDER SEARCH — Practice Selector Section
 * Replaces the services grid on the homepage.
 * Two-panel layout: Alder Search (executive search) + Alder Talent (senior leadership).
 */

import { useEffect, useRef } from "react";
import { Link } from "wouter";

export default function PracticeSelectorSection() {
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const allEls = [headerRef.current, ...panelRefs.current];
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.7s ease-out ${i * 150}ms, transform 0.7s ease-out ${i * 150}ms`;
      observer.observe(el);
    });


    // Fallback: ensure all elements become visible after 2s regardless
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  const tagStyle = {
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontWeight: 500,
    fontSize: "0.68rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    background: "oklch(0.952 0.012 80)",
    color: "oklch(0.32 0.07 155)",
    borderRadius: "2px",
    padding: "4px 10px",
  };

  return (
    <section
      id="practices"
      className="py-28"
      style={{ background: "oklch(0.978 0.006 80)" }}
    >
      <div className="container">
        {/* Section Header */}
        <div ref={headerRef} className="mb-14">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
            <span
              className="font-ui text-xs tracking-widest uppercase"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.18em",
              }}
            >
              What We Do
            </span>
          </div>
          <h2
            className="font-display max-w-2xl"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              color: "oklch(0.245 0.065 155)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
            }}
          >
            Two Practices. One Standard.
          </h2>
        </div>

        {/* Two-panel grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Panel 1 — Alder Search */}
          <div
            ref={(el) => { panelRefs.current[0] = el; }}
            className="group relative p-10 flex flex-col"
            style={{
              background: "white",
              border: "1px solid oklch(0.88 0.018 80)",
              borderRadius: "4px",
              borderTop: "3px solid oklch(0.62 0.085 75)",
            }}
          >
            {/* Eyebrow */}
            <div
              className="font-ui text-xs mb-4"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 400,
                fontSize: "0.65rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Executive Search
            </div>

            {/* Heading */}
            <h3
              className="font-display mb-4"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                color: "oklch(0.245 0.065 155)",
                lineHeight: 1.2,
                letterSpacing: "-0.015em",
              }}
            >
              C-Suite &amp; Board
            </h3>

            {/* Body */}
            <p
              className="font-body mb-7 flex-1"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 400,
                fontSize: "0.95rem",
                lineHeight: 1.8,
                color: "oklch(0.45 0.015 155)",
              }}
            >
              Every search is retained and exclusive. We do not work on contingency.
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {["CPO", "Chief Product Officer", "Board (Product)"].map((tag) => (
                <span key={tag} style={tagStyle}>{tag}</span>
              ))}
            </div>

            {/* CTA */}
            <Link
              href="/executive-search"
              className="font-ui text-sm font-500 inline-flex items-center gap-2 transition-colors duration-200"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "oklch(0.245 0.065 155)",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.245 0.065 155)";
              }}
            >
              Explore Alder Search
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>

          {/* Panel 2 — Alder Talent */}
          <div
            ref={(el) => { panelRefs.current[1] = el; }}
            className="group relative p-10 flex flex-col"
            style={{
              background: "oklch(0.245 0.065 155)",
              border: "1px solid oklch(0.32 0.07 155)",
              borderRadius: "4px",
              borderTop: "3px solid oklch(0.62 0.085 75)",
            }}
          >
            {/* Eyebrow */}
            <div
              className="font-ui text-xs mb-4"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 400,
                fontSize: "0.65rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Senior Leadership
            </div>

            {/* Heading */}
            <h3
              className="font-display mb-4"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                color: "oklch(0.952 0.012 80)",
                lineHeight: 1.2,
                letterSpacing: "-0.015em",
              }}
            >
              Head of, VP &amp; Senior IC Level
            </h3>

            {/* Body */}
            <p
              className="font-body mb-7 flex-1"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 400,
                fontSize: "0.95rem",
                lineHeight: 1.8,
                color: "oklch(0.75 0.02 155)",
              }}
            >
              We place the specialist product operators and senior individual contributors who sit beneath and beside the C-suite. Head of Product, VP Product, and senior IC practitioners including Staff, Principal, and Lead Product Managers in VC- and PE-backed businesses. The same retained standard as every Alder Search mandate.
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {["Head of Product", "VP Product", "Staff/Principal PM", "Lead Product Manager"].map((tag) => (
                <span
                  key={tag}
                  style={{
                    ...tagStyle,
                    background: "oklch(0.32 0.07 155)",
                    color: "oklch(0.72 0.09 75)",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA */}
            <Link
              href="/senior-leadership"
              className="font-ui text-sm font-500 inline-flex items-center gap-2 transition-colors duration-200"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "oklch(0.72 0.09 75)",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.015 80)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)";
              }}
            >
              Explore Alder Talent
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
